import clsx from 'clsx'
import { useRef, type ComponentPropsWithRef, type ReactElement } from 'react'

import { IconSprites, LogoSprites } from '@constants/enums/sprites'

import { Button } from '@components/ui-core/button/button'
import { Column } from '@components/ui-core/column/column'
import { ContentWrapper } from '@components/ui-core/content-wrapper/content-wrapper'
import { Icon } from '@components/ui-core/icon/icon'
import { Link } from '@components/ui-core/link/link'
import { Logo } from '@components/ui-core/logo/logo'
import { NavLinks } from '@components/ui-core/nav-links/nav-links'

import { type NavLinkItem } from '../types'

import styles from './header.module.css'

export enum HeaderType {
  Global = 'Global',
  NoNavigation = 'No Navigation',
}

interface HeaderProps extends ComponentPropsWithRef<'header'> {
  navLinks?: NavLinkItem[]
  ctaContent?: ReactElement
  type?: HeaderType
}

type HeaderContainerProps = HeaderProps & {
  onLogoClick?: () => void
}

function HeaderContainer({
  children,
  className,
  onLogoClick,
  navLinks,
  ...rest
}: HeaderContainerProps): ReactElement {
  return (
    <header className={clsx(styles.header, className)} {...rest}>
      <ContentWrapper className={styles.headerContent}>
        <Link href="/" className={styles.logo} onClick={onLogoClick}>
          <Logo variant={LogoSprites.Superhuman} aria-label="Superhuman Home" />
        </Link>
        {navLinks?.length ? <NavLinks navLinks={navLinks} className={styles.nav} /> : null}
        <div className={styles.ctas}>{children}</div>
      </ContentWrapper>
    </header>
  )
}

export function Header({ navLinks, ctaContent, type, ...rest }: HeaderProps): ReactElement {
  const dialog = useRef<HTMLDialogElement>(null)
  const noNav = type === HeaderType.NoNavigation

  function handleMenuOpen() {
    dialog.current?.showModal()
  }

  function handleMenuClose() {
    dialog.current?.setAttribute('closing', '')
    dialog.current?.addEventListener(
      'animationend',
      () => {
        dialog.current?.close()
        dialog.current?.removeAttribute('closing')
      },
      { once: true },
    )
  }

  return (
    <HeaderContainer
      navLinks={noNav ? [] : navLinks}
      className={noNav ? styles.noNavLinks : undefined}
      {...rest}
    >
      <div className={styles.ctaButtons}>{ctaContent}</div>

      {!noNav && (
        <>
          <div className={styles.menuButton}>
            <Button category="unstyled" onClick={handleMenuOpen}>
              <Icon variant={IconSprites.Menu} aria-label="Open navigation menu" />
            </Button>
          </div>

          <dialog ref={dialog} className={styles.dialog}>
            <Column className={styles.dialogContent}>
              <HeaderContainer onLogoClick={handleMenuClose}>
                <Button category="unstyled" onClick={handleMenuClose}>
                  <Icon variant={IconSprites.Close} aria-label="Dismiss navigation menu" />
                </Button>
              </HeaderContainer>
              <div className={styles.dialogBody}>
                {navLinks?.length && (
                  <NavLinks
                    navLinks={navLinks}
                    className={styles.navSecondary}
                    category="secondary"
                  />
                )}
              </div>
              <footer className={styles.dialogFooter}>{ctaContent}</footer>
            </Column>
          </dialog>
        </>
      )}
    </HeaderContainer>
  )
}
